import {
  createFileRoute,
  notFound,
  retainSearchParams,
  stripSearchParams,
} from '@tanstack/react-router';
import { fallback } from '@tanstack/router-zod-adapter';
import { z } from 'zod';
import { TradePage } from '../../components/TradePage/TradePage';
import { ASSET_ROUTE_URL } from '../../constants/routes';
import { productsQuery } from '../../hooks/useProducts';

const searchDefaults = {
  orderType: 'market',
  side: 'long',
  tableTab: 'positions',
  chartTab: 'price',
  orderbookTab: 'orderbook',
  showAllMarkets: true,
  mobileTab: 'chart',
} as const;

const assetSearchSchema = z.object({
  orderType: fallback(
    z.enum(['limit', 'market']).default(searchDefaults.orderType),
    searchDefaults.orderType
  ),
  side: fallback(z.enum(['long', 'short']).default(searchDefaults.side), searchDefaults.side),
  tableTab: fallback(
    z
      .enum(['positions', 'openOrders', 'orderHistory', 'tradeHistory', 'pnl'])
      .default(searchDefaults.tableTab),
    searchDefaults.tableTab
  ),
  chartTab: fallback(
    z.enum(['price', 'funding', 'depth', 'details']).default(searchDefaults.chartTab),
    searchDefaults.chartTab
  ),
  orderbookTab: fallback(
    z.enum(['orderbook', 'trades']).default(searchDefaults.orderbookTab),
    searchDefaults.orderbookTab
  ),
  mobileTab: fallback(
    z.enum(['chart', 'trade']).default(searchDefaults.mobileTab),
    searchDefaults.mobileTab
  ),
  showAllMarkets: fallback(
    z.coerce.boolean().default(searchDefaults.showAllMarkets),
    searchDefaults.showAllMarkets
  ),
});

export const Route = createFileRoute(ASSET_ROUTE_URL)({
  component: TradePage,
  validateSearch: assetSearchSchema,
  search: {
    middlewares: [stripSearchParams(searchDefaults), retainSearchParams(true)],
  },
  loader: async ({ context: { queryClient }, params: { asset } }) => {
    const { queryKey, queryFn } = productsQuery();
    try {
      const products = await queryClient.ensureQueryData({ queryKey, queryFn });

      const product = products?.find((product) => product.ticker === asset);
      if (!product) {
        throw notFound();
      }
      return { product };
    } catch (error) {
      if (error && typeof error === 'object' && 'isNotFound' in error) {
        throw error;
      }
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to fetch product data: ${errorMessage}`);
    }
  },
  staleTime: Infinity,
});
